"use client";

import type { LucideIcon, LucideProps } from "lucide-react";
import { type ReactElement, forwardRef } from "react";
import { cn } from "../utils";
import {
  AGENT_MARK_PATH,
  AGENT_MARK_TIGHT_VIEWBOX,
  AGENT_MARK_VIEWBOX_SIZE,
} from "./agent-mark-geometry";

export {
  AGENT_MARK_LETTER_CENTER_Y_PERCENT,
  AGENT_MARK_PATH,
  AGENT_MARK_TIGHT_VIEWBOX,
  AGENT_MARK_VIEWBOX_SIZE,
} from "./agent-mark-geometry";

/** Stored icon value for {@link IconOrEmoji} and home quick-access rows. */
export const AGENT_MARK_ICON_VALUE = "haus:agent-mark";

/** Same shape as agent avatars — rounded triangle pointing up (40×40 viewBox). */
export const AGENT_ROUNDED_TRIANGLE_PATH: string =
  "M20 3C23 3 25 5 26.5 7.5L37.5 27C39 30 39 33.5 37.5 35.5C36 37.5 33.5 38 31 38H9C6.5 38 4 37.5 2.5 35.5C1 33.5 1 30 2.5 27L13.5 7.5C15 5 17 3 20 3Z";

export type AgentMarkIconVariant = "filled" | "outline";

interface AgentMarkIconProps {
  readonly className?: string;
  readonly size?: number;
  /** The one agent triangle ({@link AGENT_MARK_PATH}); omit only for the legacy soft path. */
  readonly proportionalCorners?: boolean;
  /** `outline` = stroke only (no fill). */
  readonly variant?: AgentMarkIconVariant;
  /** Pin triangle base to the SVG box bottom (`preserveAspectRatio` YMax). */
  readonly alignBottomInBox?: boolean;
}

export const AgentMarkIcon = forwardRef<SVGSVGElement, AgentMarkIconProps>(
  function AgentMarkIcon(
    {
      className,
      size,
      proportionalCorners = false,
      variant = "filled",
      alignBottomInBox,
    },
    ref,
  ): ReactElement {
    const sizeStyle = size != null ? { width: size, height: size } : undefined;
    const pathD: string = proportionalCorners
      ? AGENT_MARK_PATH
      : AGENT_ROUNDED_TRIANGLE_PATH;

    return (
      <svg
        ref={ref}
        viewBox={
          proportionalCorners
            ? AGENT_MARK_TIGHT_VIEWBOX
            : `0 0 ${AGENT_MARK_VIEWBOX_SIZE} ${AGENT_MARK_VIEWBOX_SIZE}`
        }
        style={sizeStyle}
        className={cn(
          "shrink-0",
          size == null ? "h-[1em] w-[1em]" : undefined,
          className,
        )}
        preserveAspectRatio={alignBottomInBox ? "xMidYMax meet" : undefined}
        overflow={variant === "outline" ? "visible" : undefined}
        aria-hidden
      >
        <path
          d={pathD}
          fill={variant === "outline" ? "none" : "currentColor"}
          stroke={variant === "outline" ? "currentColor" : undefined}
          strokeWidth={variant === "outline" ? 1.25 : undefined}
          vectorEffect={
            variant === "outline" ? "non-scaling-stroke" : undefined
          }
          strokeLinejoin={variant === "outline" ? "round" : undefined}
        />
      </svg>
    );
  },
);

AgentMarkIcon.displayName = "AgentMarkIcon";

/** Drop-in for Lucide `icon` slots (nav, tabs, rule builders). */
export const AgentMarkLucideIcon: LucideIcon = forwardRef<
  SVGSVGElement,
  LucideProps
>(function AgentMarkLucideIcon({ className, size }, ref): ReactElement {
  const numericSize: number | undefined =
    typeof size === "number" ? size : undefined;
  const sizeStyle =
    numericSize != null
      ? { width: numericSize, height: numericSize }
      : undefined;

  return (
    <svg
      ref={ref}
      viewBox={AGENT_MARK_TIGHT_VIEWBOX}
      style={sizeStyle}
      className={cn(
        "shrink-0",
        numericSize == null ? "h-[1em] w-[1em]" : undefined,
        className,
      )}
      aria-hidden
    >
      <path d={AGENT_MARK_PATH} fill="currentColor" />
    </svg>
  );
});

AgentMarkLucideIcon.displayName = "AgentMarkLucideIcon";

/** Outline triangle — same look as bookmark bar (`haus:agent-mark` via IconOrEmoji). */
export const AgentMarkOutlineLucideIcon: LucideIcon = forwardRef<
  SVGSVGElement,
  LucideProps
>(function AgentMarkOutlineLucideIcon({ className, size }, ref): ReactElement {
  const numericSize: number | undefined =
    typeof size === "number" ? size : undefined;

  return (
    <AgentMarkIcon
      ref={ref}
      className={className}
      size={numericSize}
      variant="outline"
      proportionalCorners
    />
  );
});

AgentMarkOutlineLucideIcon.displayName = "AgentMarkOutlineLucideIcon";
